/**
 * GovCampanhas — Comunicados Oficiais em massa (IPTU, vacinação, editais).
 *
 * v0.99.9 · Wave 2 · Fev/2026 — reusa `/api/campaigns` do backend.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Plus, ArrowClockwise, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";

const TEMPLATES = [
  { code: "iptu", name: "Cobrança IPTU", desc: "Lembrete de vencimento com PIX/boleto anexado" },
  { code: "vacina", name: "Campanha de Vacinação", desc: "Convite para vacinação por faixa etária" },
  { code: "edital", name: "Novo Edital", desc: "Divulgação de concurso ou processo seletivo" },
  { code: "coleta", name: "Coleta Seletiva", desc: "Aviso de dias/horários por bairro" },
  { code: "obra", name: "Comunicado de Obra", desc: "Bloqueio de via ou serviço afetado" },
  { code: "assembleia", name: "Assembleia/Audiência", desc: "Convocação para participação popular" },
];

export default function GovCampanhas() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setRows(Array.isArray(data) ? data : (data?.items || []));
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="gov-campanhas-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-medium mb-1">Comunicação</div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <Megaphone size={22} className="text-emerald-700" />
            Comunicados Oficiais
          </h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">Disparo em massa via WhatsApp para grupos de cidadãos segmentados.</p>
        </div>
        <button data-testid="campanhas-new" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-[13px] font-medium">
          <Plus size={13} weight="bold" /> Novo comunicado
        </button>
      </div>

      {/* Templates prontos */}
      <div className="mb-6">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Modelos prontos</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TEMPLATES.map(t => (
            <button key={t.code} data-testid={`campaign-tpl-${t.code}`} className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-400 hover:shadow-sm transition-all">
              <div className="font-heading font-semibold text-slate-900 text-[13px]">{t.name}</div>
              <div className="text-[11.5px] text-slate-500 mt-1 leading-relaxed">{t.desc}</div>
              <div className="mt-2 text-[11px] text-emerald-700 font-medium">Usar este modelo →</div>
            </button>
          ))}
        </div>
      </div>

      {/* Histórico */}
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Histórico</div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        {loading && <div className="text-center text-[12.5px] text-slate-400 py-6">Carregando…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-center py-8 text-[12.5px] text-slate-500">
            <Megaphone size={26} className="mx-auto mb-2 text-slate-300" />
            Nenhum comunicado disparado ainda. Use um modelo acima para começar.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {rows.map(c => (
              <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-[13px]">{c.name || c.title}</div>
                  <div className="text-[11.5px] text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleString("pt-BR") : ""}</div>
                </div>
                <span className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 font-medium">
                  {c.status || "rascunho"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
